import React, { useEffect } from "react";
import Checkbox, { CheckBoxDisplay, CheckboxGroup } from "../../components/data_entry/checkbox";
import Input from "../../components/data_entry/input";
import QuestionFieldsetLegend, { StyledFieldset } from "./components/question_fieldset_legend";
import { QuestionProps } from "../survey";
import { Option, getOptionObjectFromText } from "./question_helpers";
import nextId from "react-id-generator";
import MultiSelectQuestionWrapper, {
  MultiSelectOption,
} from "../../components/base_ui/higher_order_components/questions/multiselect_question_wrapper";
import { getOptionOid } from "./question_helpers";
import InputQuestionWrapper from "../../components/base_ui/higher_order_components/questions/input_question_wrapper";

export interface Answer {
  standard: (string | Option | undefined)[];
  writeIn?: string;
}

interface OtherOption extends Option {
  optional?: boolean;
}

export interface Customizations {
  prompt: string;
  options: (string | Option)[];
  withOtherOption?: OtherOption;
  optional?: boolean;
  description?: string;
}

export default function MultiSelect({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, Customizations>) {
  const label = customizations.prompt;
  const id = "multi-select-" + nextId();
  const secondaryText = customizations.description ?? "";
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const otherOption = customizations.withOtherOption;
  let options = customizations.options;

  if (otherOption) {
    options = options.concat(otherOption);
  }

  const selectedOptions = currentAnswer ? currentAnswer.standard : [];
  const answerIsValid =
    (optional &&
      (!otherOption ||
        !selectedOptions.includes(getOptionObjectFromText(otherOption.text, options)))) ||
    ((currentAnswer?.standard.length || 0) > 0 &&
      (!(
        otherOption &&
        !otherOption.optional &&
        selectedOptions.includes(getOptionObjectFromText(otherOption.text, options))
      ) ||
        (currentAnswer?.writeIn != null && currentAnswer.writeIn.length > 0)));
  const followUpErrorMessage =
    showValidation && !answerIsValid ? "Please complete the required field." : "";

  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  const deprecatedMultiSelect = (
    <div className="multi-select">
      <StyledFieldset name={label}>
        <QuestionFieldsetLegend
          legend={label}
          description={secondaryText}
          questionIsOptional={optional}
          data-test="multi-select-question-label"
        />
        <CheckboxGroup display={CheckBoxDisplay.normal}>
          {options.map((option) => {
            const label = typeof option === "string" ? option : option.text;
            return (
              <Checkbox
                dataTest="multi-select-checkbox"
                label={label}
                key={label}
                checked={selectedOptions.includes(getOptionObjectFromText(label, options))}
                htmlFor={id}
                onChange={() => {
                  const index = selectedOptions.indexOf(getOptionObjectFromText(label, options));
                  if (index > -1) {
                    // The answer is already checked, uncheck it.
                    updateAnswer({
                      standard: [
                        ...selectedOptions.slice(0, index),
                        ...selectedOptions.slice(index + 1),
                      ],
                      writeIn: option === otherOption ? undefined : currentAnswer?.writeIn,
                    });
                  } else {
                    // The answer is not checked yet, check it.
                    updateAnswer({
                      ...currentAnswer,
                      standard: [...selectedOptions, getOptionObjectFromText(label, options)],
                    });
                  }
                }}
              />
            );
          })}
        </CheckboxGroup>
        {otherOption &&
          selectedOptions.includes(getOptionObjectFromText(otherOption.text, options)) && (
            <Input
              type="text"
              value={currentAnswer && currentAnswer.writeIn ? currentAnswer.writeIn : ""}
              id={id}
              questionIsOptional={otherOption.optional ?? false}
              onChange={({ currentTarget: { value: updatedWriteIn } }) =>
                updateAnswer({
                  standard: selectedOptions,
                  writeIn: updatedWriteIn,
                })
              }
            />
          )}
      </StyledFieldset>
    </div>
  );

  const multiSelect = (
    <>
      <MultiSelectQuestionWrapper
        questionPrompt={label}
        optional={optional}
        secondaryText={secondaryText}
        showErrors={showValidation && !answerIsValid && !selectedOptions.length}
        options={options.map((option) => {
          const optionLabel = typeof option === "string" ? option : option.text;
          return {
            id: getOptionOid(option) || "",
            label: optionLabel || "",
            value: selectedOptions.includes(getOptionObjectFromText(optionLabel, options)),
          };
        })}
        onChange={(newAnswer: MultiSelectOption) => {
          const option = typeof newAnswer === "string" ? newAnswer : newAnswer.label;
          const optionObject = getOptionObjectFromText(option, options);
          const index = selectedOptions.indexOf(optionObject);

          if (index > -1) {
            // The answer is already checked, uncheck it.
            updateAnswer({
              standard: [...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)],
              writeIn: optionObject === otherOption ? undefined : currentAnswer?.writeIn,
            });
          } else {
            // The answer is not checked yet, check it.
            updateAnswer({
              ...currentAnswer,
              standard: [...selectedOptions, optionObject],
            });
          }
        }}
        setAbleToSubmit={setAbleToSubmit}
        dataTest={id}
      />
      {otherOption &&
        selectedOptions.includes(getOptionObjectFromText(otherOption.text, options)) && (
          <InputQuestionWrapper
            ariaLabel={`Follow-up input field for: ${otherOption.text}`}
            optional={false} // it might be required but we don't want to show the optional label
            value={currentAnswer && currentAnswer.writeIn ? currentAnswer.writeIn : ""}
            onChange={({ currentTarget: { value: updatedWriteIn } }) =>
              updateAnswer({
                standard: selectedOptions,
                writeIn: updatedWriteIn,
              })
            }
            errorMessage={followUpErrorMessage}
            dataTest={`${id}-follow-up-input`}
          />
        )}
    </>
  );
  return showRefreshedComponents ? multiSelect : deprecatedMultiSelect;
}
